import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import i18n from "../../i18";
import Nursery from "../Images/Nursery.png";
import PrimaryShool from "../Images/PrimaryShool.png";
import SecondarySchool from "../Images/SecondarySchool.png";
const GetUnpaidStudentsReport = () => {
  const [students, setStudents] = React.useState([]);
  const [filteredStudents, setFilteredStudents] = React.useState([]);
  const [selectedClass, setSelectedClass] = React.useState("all");
  const [selectedSection, setSelectedSection] = React.useState("all");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const token = localStorage.getItem("token");
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = React.useState(Nursery);
  const user = JSON.parse(localStorage.getItem("user"));
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          // "https://school.thehoodiecat.xyz/api/reports/unpaid-students-report",
          "https://api.clever-school.com/api/reports/unpaid-students-report",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        const studentsArray = Object.values(data);
        setStudents(studentsArray);
        setFilteredStudents(studentsArray);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  React.useEffect(() => {
    const loadUserData = () => {
      const branchId = user?.branch_id || 2;
      const branchImages = {
        1: Nursery,
        2: PrimaryShool,
        3: SecondarySchool,
      };
      setSelectedImage(branchImages[branchId] || Nursery);
    };

    loadUserData();
  }, [user]);

  const parseField = (field) => {
    try {
      return JSON.parse(field).en;
    } catch {
      return field;
    }
  };

  const currencyFormatter = (value) =>
    parseFloat(value || 0).toLocaleString(undefined);

  const applyFilters = (classFilter, sectionFilter) => {
    const filtered = students.filter((student) => {
      const matchesClass =
        classFilter === "all" || parseField(student.class) === classFilter;
      const matchesSection =
        sectionFilter === "all" ||
        parseField(student.section) === sectionFilter;
      return matchesClass && matchesSection;
    });
    setFilteredStudents(filtered);
  };

  const handleClassFilterChange = (event) => {
    const value = event.target.value;
    setSelectedClass(value);
    applyFilters(value, selectedSection);
  };

  const handleSectionFilterChange = (event) => {
    const value = event.target.value;
    setSelectedSection(value);
    applyFilters(selectedClass, value);
  };

  const columns = [
    {
      title: "#",
      render: (_, __, index) => index + 1,
    },
    { title: t("name"), dataIndex: "name" },
    {
      title: t("class"),
      render: (_, row) => parseField(row.class),
    },
    {
      title: t("section"),
      render: (_, row) => parseField(row.section),
    },
    { title: t("phoneNumber"), dataIndex: "phone_number" },
    { title: t("gender"), dataIndex: "gender" },
    {
      title: t("tuitionFee"),
      render: (_, row) => currencyFormatter(row.tuition_fee),
    },
    {
      title: t("discountedStudents"),
      render: (_, row) => currencyFormatter(row.tuition_discount),
    },
    {
      title: t("totalPaid"),
      render: (_, row) => currencyFormatter(row.total_paid),
    },
    {
      title: t("remainingBalance"),
      render: (_, row) => currencyFormatter(row.total_remaining),
    },
  ];

  // Print styles with large font sizes (same as GetStudentReport)
  const printStyles = `
@media print {
     * {
        font-size: 24px !important;
      }
      
     html, body, div, span, table, tr, td, th, p, h1, h2, h3, h4, h5, h6 {
        font-size: 24px !important;
      }
      
     body, html {
        width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        font-size: 24px !important;
        line-height: 1.5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        background-color: white !important;
        direction: ${isRTL ? 'rtl' : 'ltr'} !important;
        text-align: ${isRTL ? 'right' : 'left'} !important;
      }

    body * {
      visibility: hidden;
      font-size: 24px !important;
    }

    .print-container, .print-container * {
      visibility: visible;
      background-color: white !important;
      font-size: 24px !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
      text-align: ${isRTL ? 'right' : 'left'} !important;
    }

    .print-container {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: auto;
      padding: 0;
      margin: 0;
    }

    .print-header {
      display: block !important;
      text-align: ${isRTL ? 'right' : 'center'};
      margin-bottom: 20px;
      page-break-after: avoid;
      font-size: 24px !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }

    .print-title {
      font-size: 32px !important;
      font-weight: bold !important;
      color: #333 !important;
      margin: 20px 0 !important;
    }

    .no-print {
      display: none !important;
    }

    .table-container {
      width: 100% !important;
      margin: 0 !important;
      overflow: visible !important;
      page-break-inside: auto;
    }

    table {
      width: 100% !important;
      border-collapse: separate !important;
      border-spacing: 0 !important;
      font-size: 24px !important;
      border: none !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
      border-radius: 8px !important;
      overflow: hidden !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }

    th {
      background: #000080 !important;
      color: #ffffff !important;
      font-weight: 700 !important;
      font-size: 20px !important;
      text-align: ${isRTL ? 'right' : 'center'} !important;
      text-transform: uppercase !important;
      letter-spacing: 0.8px !important;
      border-bottom: 2px solid #dee2e6 !important;
      position: relative !important;
    }
    th, th * {
      background: #000080 !important;
      color: #ffffff !important;
      font-size: 20px !important;
    }
    th:first-child {
      border-top-left-radius: 8px !important;
    }
    th:last-child {
      border-top-right-radius: 8px !important;
    }
    thead {
      display: table-header-group !important;
    }
    tbody tr:nth-child(even) {
      background-color: #fafbfc !important;
    }
    tbody tr:nth-child(odd) {
      background-color: white !important;
    }
    tbody tr:hover {
      background-color: #f8f9fa !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
    }
    .total-row {
      background: #ffffff !important;
      color: #2c3e50 !important;
      font-weight: 700 !important;
      border-top: 3px solid #2196f3 !important;
      border-bottom: none !important;
      position: relative !important;
    }
    .total-row::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      height: 3px !important;
      background: linear-gradient(90deg, #2196f3 0%, #1976d2 100%) !important;
    }
    .total-row td {
      background: transparent !important;
      color: #2c3e50 !important;
      font-weight: 700 !important;
      font-size: 24px !important;
      border: none !important;
      border-top: 3px solid #2196f3 !important;
      padding: 20px !important;
      text-align: ${isRTL ? 'right' : 'left'} !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }
    .total-row td:first-child {
      border-bottom-left-radius: 8px !important;
    }
    .total-row td:last-child {
      border-bottom-right-radius: 8px !important;
    }
    @page {
      size: A4;
      margin: 8mm;
    }
    .print-container th,
    .print-container th * {
      font-size: 20px !important;
      background: #000080 !important;
      color: #ffffff !important;
    }
    .print-container td,
    .print-container td * {
      font-size: 24px !important;
    }
    .print-container table {
      font-size: 24px !important;
    }
    .print-container table td {
      font-size: 24px !important;
    }
    .print-container table th {
      font-size: 20px !important;
    }

    tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }

    td, th {
      padding: 16px 20px !important;
      border: none !important;
      font-size: 24px !important;
      text-align: ${isRTL ? 'right' : 'left'} !important;
      vertical-align: middle !important;
      border-bottom: 1px solid #f0f0f0 !important;
      direction: ${isRTL ? 'rtl' : 'ltr'} !important;
    }
    td {
      font-size: 24px !important;
    }
    .print-container td, .print-container th {
      font-size: 24px !important;
    }
    .print-container th {
      font-size: 20px !important;
    }
  }

  .print-header {
    display: none;
  }
`;

  const handlePrint = () => {
    window.print();
  };

  const getCurrentAcademicYear = () => {
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${currentYear + 1}`;
  };

  const branchTitleMap = {
    1: "باخچەی منداڵانی کلیڤەری ناحکومی",
    2: "ناوەندیی کلیڤەری ناحکومی",
    3: "ئامادەی کلیڤەری ناحکومی",
  };
  const branchTitle = branchTitleMap[user?.branch_id] || "قوتابخانەی کلیڤەری بنەڕەتی ناحکومی";

  // eslint-disable-next-line no-unused-vars
  const totals = filteredStudents.reduce(
    (acc, student) => ({
      tuitionFee: acc.tuitionFee + parseFloat(student.tuition_fee || 0),
      discount: acc.discount + parseFloat(student.tuition_discount || 0),
      paid: acc.paid + parseFloat(student.total_paid || 0),
      remaining: acc.remaining + parseFloat(student.total_remaining || 0),
    }),
    { tuitionFee: 0, discount: 0, paid: 0, remaining: 0 }
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h6" color="error" sx={{ fontWeight: "bold" }}>
          Error: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2, marginBottom: 10 }}>
      <style>{printStyles}</style>
      <Box
        className="print-container"
        sx={{
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
          padding: 2,
          marginBottom: 17,
          "@media print": {
            padding: 0,
            margin: 0,
            overflow: "visible !important",
            height: "auto !important",
          },
        }}
      >
        {/* Print Header */}
        <div className="print-header">
          <div
            style={{
              display: "flex",
              margin: "0 auto",
              padding: "0",
            }}
          >
            <div
              style={{
                flex: 1,
                textAlign: "right",
                marginRight: "40px",
              }}
            >
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  marginBottom: "10px",
                }}
              >
                {branchTitle}
              </div>

              <div style={{ fontSize: "16px", marginBottom: "15px" }}>
                ساڵی خوێندن ( {getCurrentAcademicYear()} )
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100px",
                marginLeft: "40px",
              }}
            >
              <img
                src={selectedImage}
                alt="School Logo"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
          <hr
            style={{
              borderColor: "#000",
              margin: "20px 0",
              textAlign: "right",
            }}
          />
          <div
            className="print-title"
            style={{
              color: "red",
              fontSize: "40px",
              fontWeight: "bold",
            }}
          >
            {t("unpaid_students_report")}
          </div>
        </div>
      {/* <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          color: "#2e7d32",
          mb: 4,
          textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
        }}
      >
        Unpaid Students Report
      </Typography> */}

      <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("class")}</InputLabel>
            <Select
              value={selectedClass}
              onChange={handleClassFilterChange}
              label={t("class")}
            >
              <MenuItem value="all">All Classes</MenuItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                <MenuItem key={n} value={n.toString()}>{`Class ${n}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("section")}</InputLabel>
            <Select
              value={selectedSection}
              onChange={handleSectionFilterChange}
              label={t("section")}
            >
              <MenuItem value="all">All Sections</MenuItem>
              {["A", "B", "C", "D", "E", "F", "G", "H", "I"].map((sec) => (
                <MenuItem key={sec} value={sec}>{`Section ${sec}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Print Button */}
      <Box textAlign="center" mb={2} className="no-print">
        <Button
          variant="contained"
          onClick={handlePrint}
          sx={{
            width: 200,
            fontWeight: "bold",
            backgroundColor: "#2e7d32",
            "&:hover": { backgroundColor: "#1b5e20" },
          }}
        >
          {t("Print")}
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
          border: "1px solid #e9ecef",
          backgroundColor: "white",
          direction: isRTL ? "rtl" : "ltr",
          "@media print": {
            boxShadow: "0 1px 3px rgba(0,0,0,0.1) !important",
            border: "none !important",
            overflow: "visible !important",
            height: "auto !important",
            direction: isRTL ? "rtl" : "ltr",
            borderRadius: "8px !important",
          },
        }}
        className="table-container"
      >
        <Table sx={{ direction: isRTL ? "rtl" : "ltr" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.dataIndex || column.title}
                  sx={{
                    background: "#000080",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "13px",
                    textAlign: column.align || (isRTL ? 'right' : 'center'),
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    padding: "16px 20px",
                    border: "none",
                    borderBottom: "2px solid #dee2e6",
                    width: column.width || "auto",
                    minWidth: column.width || "auto",
                    maxWidth: column.width || "auto",
                    "@media print": {
                      background: "#000080 !important",
                      color: "#ffffff !important",
                      fontSize: "20px !important",
                      border: "none !important",
                      borderBottom: "2px solid #dee2e6 !important",
                      width: column.width || "auto !important",
                      minWidth: column.width || "auto !important",
                      maxWidth: column.width || "auto !important",
                    },
                  }}
                >
                  {column.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredStudents.map((student, index) => (
              <TableRow
                key={student.id}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#fafbfc" : "white",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  },
                  transition: "all 0.2s ease",
                  "&:last-child td": { borderBottom: 0 }
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.dataIndex || column.title}
                    sx={{
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      fontSize: "20px",
                      padding: "16px 20px",
                      textAlign: column.align || (isRTL ? 'right' : 'left'),
                      verticalAlign: "middle",
                      transition: "all 0.2s ease",
                      width: column.width || "auto",
                      minWidth: column.width || "auto",
                      maxWidth: column.width || "auto",
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                        transform: "translateY(-1px)",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      },
                      "@media print": {
                        padding: "16px 20px !important",
                        border: "none !important",
                        borderBottom: "1px solid #f0f0f0 !important",
                        fontSize: "24px !important",
                        width: column.width || "auto !important",
                        minWidth: column.width || "auto !important",
                        maxWidth: column.width || "auto !important",
                      },
                    }}
                  >
                    {column.render
                      ? column.render(student, student, index)
                      : student[column.dataIndex]}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {/* Total Row */}
            <TableRow
              className="total-row"
              sx={{
                backgroundColor: "#ffffff",
                "@media print": {
                  backgroundColor: "#ffffff !important",
                },
              }}
            >
              <TableCell
                colSpan={5}
                align="right"
                sx={{
                  background: "#ffffff",
                  color: "#2c3e50",
                  fontWeight: 700,
                  fontSize: "20px",
                  padding: "20px",
                  border: "none",
                  position: "relative",
                  "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                    border: "none !important",
                    fontSize: "24px !important",
                  },
                }}
              >
                {t("total")}
              </TableCell>
              <TableCell
                sx={{
                  background: "#ffffff",
                  color: "#2c3e50",
                  fontWeight: 700,
                  fontSize: "20px",
                  padding: "20px",
                  border: "none",
                  "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                    border: "none !important",
                    fontSize: "24px !important",
                  },
                }}
              >
                {currencyFormatter(totals.tuitionFee)}
              </TableCell>
              <TableCell
                sx={{
                  background: "#ffffff",
                  color: "#2c3e50",
                  fontWeight: 700,
                  fontSize: "20px",
                  padding: "20px",
                  border: "none",
                  "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                    border: "none !important",
                    fontSize: "24px !important",
                  },
                }}
              >
                {currencyFormatter(totals.discount)}
              </TableCell>
              <TableCell
                sx={{
                  background: "#ffffff",
                  color: "#2c3e50",
                  fontWeight: 700,
                  fontSize: "20px",
                  padding: "20px",
                  border: "none",
                  "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                    border: "none !important",
                    fontSize: "24px !important",
                  },
                }}
              >
                {currencyFormatter(totals.paid)}
              </TableCell>
              <TableCell
                sx={{
                  background: "#ffffff",
                  color: "#2c3e50",
                  fontWeight: 700,
                  fontSize: "20px",
                  padding: "20px",
                  border: "none",
                  "@media print": {
                      background: "#ffffff !important",
                      color: "#2c3e50 !important",
                    border: "none !important",
                    fontSize: "24px !important",
                  },
                }}
              >
                {currencyFormatter(totals.remaining)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
    </Box>
  );
};

export default GetUnpaidStudentsReport;
